import React, { createContext, useContext, useState, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { alpha } from '@mui/material/styles';

const ThemeModeContext = createContext();

// Shared brand colors
const cyan = '#00BFFF';
const green = '#8DC63F';
const navy = '#1B2A4A';

function getTheme(mode) {
    const isDark = mode === 'dark';

    const darkBg = '#0D1421';
    const cardBg = '#141E30';
    const borderColor = isDark ? 'rgba(0, 191, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)';

    return createTheme({
        palette: {
            mode,
            primary: {
                main: cyan,
                light: '#33CCFF',
                dark: '#0099CC',
                contrastText: isDark ? '#0D1421' : '#fff',
            },
            secondary: {
                main: green,
                light: '#A4D65E',
                dark: '#6B9A2B',
                contrastText: isDark ? '#0D1421' : '#fff',
            },
            background: {
                default: isDark ? darkBg : '#F5F7FA',
                paper: isDark ? cardBg : '#FFFFFF',
            },
            text: {
                primary: isDark ? '#E8EDF5' : '#1A1A2E',
                secondary: isDark ? '#8A9BBD' : '#5A6478',
            },
            divider: borderColor,
            error: { main: '#FF5252' },
            warning: { main: '#FFB74D' },
            success: { main: green },
            info: { main: cyan },
        },
        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            h1: { fontWeight: 800, letterSpacing: '-0.02em' },
            h2: { fontWeight: 700, letterSpacing: '-0.01em' },
            h3: { fontWeight: 700 },
            h4: { fontWeight: 600 },
            h5: { fontWeight: 600 },
            h6: { fontWeight: 600 },
            button: { fontWeight: 600, textTransform: 'none' },
        },
        shape: { borderRadius: 12 },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: isDark ? {
                        backgroundColor: darkBg,
                        backgroundImage: `radial-gradient(ellipse at 20% 0%, ${alpha(cyan, 0.04)} 0%, transparent 50%), radial-gradient(ellipse at 80% 100%, ${alpha(green, 0.03)} 0%, transparent 50%)`,
                        backgroundAttachment: 'fixed',
                    } : {},
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        border: `1px solid ${borderColor}`,
                        ...(isDark ? {
                            backgroundColor: cardBg,
                            backdropFilter: 'blur(12px)',
                            boxShadow: `0 4px 24px ${alpha('#000', 0.3)}`,
                        } : {
                            boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
                        }),
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                            borderColor: isDark ? alpha(cyan, 0.25) : alpha(cyan, 0.3),
                            boxShadow: isDark ? `0 8px 32px ${alpha('#000', 0.4)}` : '0 4px 16px rgba(0,0,0,0.1)',
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: { root: { backgroundImage: 'none' } },
            },
            MuiButton: {
                styleOverrides: {
                    root: { borderRadius: 8, padding: '10px 24px', fontSize: '0.95rem' },
                    contained: {
                        boxShadow: `0 2px 12px ${alpha(cyan, 0.3)}`,
                        '&:hover': { boxShadow: `0 4px 20px ${alpha(cyan, 0.4)}` },
                    },
                    containedSecondary: {
                        boxShadow: `0 2px 12px ${alpha(green, 0.3)}`,
                        '&:hover': { boxShadow: `0 4px 20px ${alpha(green, 0.4)}` },
                    },
                    outlined: {
                        borderColor: alpha(cyan, 0.4),
                        '&:hover': { borderColor: cyan, backgroundColor: alpha(cyan, 0.08) },
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: isDark ? alpha(darkBg, 0.85) : alpha('#FFFFFF', 0.9),
                        backdropFilter: 'blur(16px)',
                        borderBottom: `1px solid ${borderColor}`,
                        boxShadow: 'none',
                        color: isDark ? '#E8EDF5' : '#1A1A2E',
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundColor: isDark ? cardBg : '#FFFFFF',
                        borderRight: `1px solid ${borderColor}`,
                    },
                },
            },
            MuiChip: {
                styleOverrides: { root: { fontWeight: 500 } },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        borderColor: alpha(cyan, 0.3),
                        color: isDark ? '#8A9BBD' : '#5A6478',
                        '&.Mui-selected': {
                            backgroundColor: alpha(cyan, 0.15),
                            color: cyan,
                            borderColor: cyan,
                            '&:hover': { backgroundColor: alpha(cyan, 0.25) },
                        },
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: isDark ? alpha(cyan, 0.2) : 'rgba(0,0,0,0.15)' },
                            '&:hover fieldset': { borderColor: alpha(cyan, 0.4) },
                        },
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: { borderRadius: 4, backgroundColor: alpha(cyan, 0.1) },
                    bar: { borderRadius: 4 },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: { borderRadius: 8 },
                    standardInfo: {
                        backgroundColor: alpha(cyan, 0.1),
                        color: isDark ? '#E8EDF5' : '#1A1A2E',
                        border: `1px solid ${alpha(cyan, 0.2)}`,
                    },
                    standardSuccess: {
                        backgroundColor: alpha(green, 0.1),
                        color: isDark ? '#E8EDF5' : '#1A1A2E',
                        border: `1px solid ${alpha(green, 0.2)}`,
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: { root: { borderBottomColor: borderColor } },
            },
        },
    });
}

export function ThemeModeProvider({ children }) {
    const [mode, setMode] = useState(() => {
        try {
            return localStorage.getItem('themeMode') || 'dark';
        } catch {
            return 'dark';
        }
    });

    const toggleTheme = () => {
        setMode((prev) => {
            const next = prev === 'dark' ? 'light' : 'dark';
            try { localStorage.setItem('themeMode', next); } catch {}
            return next;
        });
    };

    const theme = useMemo(() => getTheme(mode), [mode]);

    return (
        <ThemeModeContext.Provider value={{ mode, toggleTheme }}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeModeContext.Provider>
    );
}

export function useThemeMode() {
    const ctx = useContext(ThemeModeContext);
    if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider');
    return ctx;
}
